import React, { Component } from 'react'
import { X } from 'lucide-react'

import history from '../../history'
import { Alert } from '@/components/ui/alert'
import { cn } from '@/lib/utils'

// Cognito / session error codes and messages that occur during normal
// logout, login, app-boot and silent token refresh. These must NEVER flash a
// global "Something went wrong" — they are handled by route guards / auth UI.
const BENIGN_PATTERNS = [
  'SESSION_EXPIRED',
  'NotAuthorizedException',
  'No current user',
  'PasswordResetRequiredException',
  'UserNotFoundException',
  'Refresh Token has expired',
  'Access Token has been revoked'
]

const isBenign = reason => {
  // Empty/undefined rejections are the benign authUser()/session rejects.
  if (reason == null) return true

  const code = (reason && reason.code) || ''
  const message =
    (reason && reason.message) ||
    (typeof reason === 'string' ? reason : '') ||
    ''

  if (!code && !message) return true

  return BENIGN_PATTERNS.some(
    p => code === p || message.indexOf(p) !== -1
  )
}

export default class GlobalErrorToast extends Component {
  constructor(props) {
    super(props)
    this.state = { open: false }
    this.timer = null
    this.handleRejection = this.handleRejection.bind(this)
    this.handleError = this.handleError.bind(this)
    this.dismiss = this.dismiss.bind(this)
  }

  componentDidMount() {
    window.addEventListener('unhandledrejection', this.handleRejection)
    window.addEventListener('error', this.handleError)
  }

  componentWillUnmount() {
    window.removeEventListener('unhandledrejection', this.handleRejection)
    window.removeEventListener('error', this.handleError)
    if (this.timer) clearTimeout(this.timer)
  }

  shouldSuppress(reason) {
    // Never surface on the login screen (logout/login/session-refresh churn).
    const path = history.location && history.location.pathname
    if (path === '/login') return true
    return isBenign(reason)
  }

  surface() {
    this.setState({ open: true })
    if (this.timer) clearTimeout(this.timer)
    // Auto-dismiss so a transient blip doesn't linger; still dismissible.
    this.timer = setTimeout(() => this.setState({ open: false }), 8000)
  }

  handleRejection(event) {
    if (this.shouldSuppress(event && event.reason)) return
    this.surface()
  }

  handleError(event) {
    // window 'error' fires for uncaught runtime errors (incl. event-handler
    // throws like the requestOrderCSV destructure). Ignore resource-load
    // errors (img/script) which have no `error` object.
    if (!event || !event.error) return
    if (this.shouldSuppress(event.error)) return
    this.surface()
  }

  dismiss() {
    if (this.timer) clearTimeout(this.timer)
    this.setState({ open: false })
  }

  render() {
    if (!this.state.open) return null

    return (
      <div
        className={cn(
          'fixed bottom-4 right-4 z-50 w-[min(92vw,24rem)]',
          'shadow-lg'
        )}
      >
        <Alert variant="error" className="pr-9">
          <div>
            <p className="font-medium">Something went wrong</p>
            <p className="mt-0.5 text-sm opacity-90">
              An unexpected error occurred. Please refresh and try again.
            </p>
          </div>
          <button
            type="button"
            onClick={this.dismiss}
            aria-label="Dismiss"
            className={cn(
              'absolute right-2 top-2 inline-flex h-6 w-6 items-center',
              'justify-center rounded-md text-destructive/70',
              'hover:bg-destructive/10 hover:text-destructive'
            )}
          >
            <X className="h-4 w-4" />
          </button>
        </Alert>
      </div>
    )
  }
}
